import type { DeliveryLogResponse, DeliverySummary, DeliveryStatus } from '../types/delivery';

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    if (!url.startsWith('/api/auth/') && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `HTTP ${res.status}`);
  }

  return res.json();
}

export const api = {
  auth: {
    login: (username: string, password: string) =>
      request<{ success: boolean; role: string | null; username?: string }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      }),
    me: () => request<{ username: string; role: string }>('/api/auth/me'),
    logout: () => request<{ success: boolean }>('/api/auth/logout', { method: 'POST' }),
  },

  guests: {
    list: (params?: { search?: string; field?: string; page?: number; limit?: number; pay_date?: string; registered?: string; dinner?: string }) => {
      const sp = new URLSearchParams();
      if (params?.search) sp.set('search', params.search);
      if (params?.field) sp.set('field', params.field);
      if (params?.page) sp.set('page', String(params.page));
      if (params?.limit) sp.set('limit', String(params.limit));
      if (params?.pay_date) sp.set('pay_date', params.pay_date);
      if (params?.registered) sp.set('registered', params.registered);
      if (params?.dinner) sp.set('dinner', params.dinner);
      return request<{ guests: any[]; total: number }>(`/api/guests?${sp}`);
    },
    bulkUpdate: (updates: { id: number; field: string; value: boolean }[]) =>
      request<{ updated: number }>('/api/guests/bulk', {
        method: 'PATCH',
        body: JSON.stringify({ updates }),
      }),
    importPreview: (formData: FormData) =>
      request<{ rows: any[]; errors: any[]; columns: string[]; rejectedCount?: number; existingIds?: string[]; existingCount?: number; totalReceived?: number; spam?: { directus_id: string; l_name: string; f_name: string; company: string; message: string }[] }>('/api/import/preview', {
        method: 'POST',
        body: formData,
      }),
    fetchApi: (url?: string) =>
      request<{ rows: any[]; errors: any[]; columns: string[]; rejectedCount?: number; existingIds?: string[]; existingCount?: number; totalReceived?: number; spam?: { directus_id: string; l_name: string; f_name: string; company: string; message: string }[] }>('/api/import/fetch-api', {
        method: 'POST',
        body: JSON.stringify({ url }),
      }),
    importConfirm: (rows: any[], mode: 'append' | 'replace', rejected?: { directus_id?: string; l_name?: string; f_name?: string; company?: string }[]) =>
      request<{ inserted: number; skipped: number; errors: any[] }>('/api/import/confirm', {
        method: 'POST',
        body: JSON.stringify({ rows, mode, rejected }),
      }),
    listRejected: () =>
      request<{ rejected: { directus_id: string; l_name: string; f_name: string; company: string; rejected_at: string }[] }>('/api/import/rejected'),
    restoreRejected: (directus_id: string) =>
      request<{ success: boolean }>(`/api/import/rejected/${encodeURIComponent(directus_id)}`, { method: 'DELETE' }),
    sendEmails: (ids?: number[]) =>
      request<{ queued: number; failed: number }>('/api/email/send', {
        method: 'POST',
        body: JSON.stringify({ ids: ids || [] }),
      }),
    resendEmail: (id: number) =>
      request<{ success: boolean }>(`/api/email/${id}/resend-email`, { method: 'POST' }),
    create: (data: { l_name: string; f_name: string; p_name?: string; company?: string; role?: string; country?: string; email?: string }) =>
      request<{ guest: any }>('/api/guests', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: number, data: { l_name: string; f_name: string; p_name?: string; company?: string; role?: string; country?: string; email?: string }) =>
      request<{ guest: any }>(`/api/guests/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: number) =>
      request<{ success: boolean }>(`/api/guests/${id}`, { method: 'DELETE' }),
    exportExcel: (params?: { search?: string; field?: string; pay_date?: string; registered?: string; dinner?: string }) => {
      const sp = new URLSearchParams();
      if (params?.search) sp.set('search', params.search);
      if (params?.field) sp.set('field', params.field);
      if (params?.pay_date) sp.set('pay_date', params.pay_date);
      if (params?.registered) sp.set('registered', params.registered);
      if (params?.dinner) sp.set('dinner', params.dinner);

      const a = document.createElement('a');
      a.href = `/api/guests/export?${sp}`;
      a.download = '';
      a.click();
    },
  },

  email: {
    log: (params?: { status?: string; source?: string; search?: string; page?: number; limit?: number }) => {
      const sp = new URLSearchParams();
      if (params?.status) sp.set('status', params.status);
      if (params?.source) sp.set('source', params.source);
      if (params?.search) sp.set('search', params.search);
      if (params?.page) sp.set('page', String(params.page));
      if (params?.limit) sp.set('limit', String(params.limit));
      return request<DeliveryLogResponse>(`/api/email/log?${sp}`);
    },
    logSummary: (params?: { status?: string; source?: string; search?: string }) => {
      const sp = new URLSearchParams();
      if (params?.status) sp.set('status', params.status);
      if (params?.source) sp.set('source', params.source);
      if (params?.search) sp.set('search', params.search);
      return request<DeliverySummary>(`/api/email/log/summary?${sp}`);
    },
    retry: (id: number) =>
      request<{ success: boolean; status: DeliveryStatus; error: string | null }>(`/api/email/log/${id}/retry`, { method: 'POST' }),
    resend: (guestId: number, email?: string) =>
      request<{ success: boolean; status: DeliveryStatus; error: string | null; sent_to: string }>(`/api/email/${guestId}/resend-email`, {
        method: 'POST',
        body: JSON.stringify({ email: email?.trim() || undefined }),
      }),
    retryFailed: () =>
      request<{ retried: number; succeeded: number; failed: number }>('/api/email/log/retry-failed', { method: 'POST' }),
  },

  user: {
    get: (directus_id: string) => request<{ guest: any | null }>(`/api/user/${directus_id}`),    register: (directus_id: string) =>
      request<{ success: boolean; message: string }>(`/api/user/${directus_id}/register`, {
        method: 'POST',
      }),
    dinner: (directus_id: string) =>
      request<{ success: boolean; message: string }>(`/api/user/${directus_id}/dinner`, {
        method: 'POST',
      }),
  },

  config: {
    get: () => request<{ registration_mode: string; email_subject: string; email_body: string }>('/api/config'),
    set: (updates: Record<string, string>) =>
      request<{ registration_mode: string; email_subject: string; email_body: string }>('/api/config', {
        method: 'POST',
        body: JSON.stringify(updates),
      }),
    getRegistrationMode: () => request<{ enabled: boolean }>('/api/config/registration-mode'),
    setRegistrationMode: (enabled: boolean) =>
      request<{ enabled: boolean }>('/api/config/registration-mode', {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
    cleanQr: () =>
      request<{ deleted: number }>('/api/config/clean-qr', { method: 'POST' }),
  },

  stats: {
    get: () => request<{ total: number; registered: number; dinner: number; paid: number }>('/api/stats'),
  },

  users: {
    list: () => request<{ id: number; username: string; role: string; created_at: string }[]>('/api/users'),
    create: (data: { username: string; password: string; role: string }) =>
      request<{ id: number; username: string; role: string }>('/api/users', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: number, data: { username?: string; password?: string; role?: string }) =>
      request<{ success: boolean }>(`/api/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: number) =>
      request<{ success: boolean }>(`/api/users/${id}`, { method: 'DELETE' }),
  },

  health: {
    get: () => request<{ status: string; dbLatency: number; uptime: number }>('/api/health'),
  },
};
